// © 2025 Camp Promethean, LLC. All Rights Reserved.
// Facility-scoped room / zone / crop pickers for the Hands-Free review form.

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useFacility } from "@/contexts/FacilityContext";

export interface SelectOption {
  value: string;
  label: string;
}

interface EntityOptions {
  rooms: SelectOption[];
  zones: SelectOption[];
  crops: SelectOption[];
}

const EMPTY: EntityOptions = { rooms: [], zones: [], crops: [] };

export function useHandsFreeEntityOptions() {
  const { activeFacility } = useFacility();

  const { data = EMPTY, isLoading } = useQuery({
    queryKey: ["hands-free-entities", activeFacility?.id],
    queryFn: async (): Promise<EntityOptions> => {
      if (!activeFacility?.id) return EMPTY;

      const [roomsRes, cropsRes, strainsRes] = await Promise.all([
        supabase
          .from("rooms")
          .select("id, room_name, zones ( id, zone_number )")
          .eq("facility_id", activeFacility.id)
          .order("room_name"),
        supabase
          .from("crops")
          .select("id, code, strain_id")
          .order("code"),
        supabase.from("strains").select("id, name"),
      ]);

      if (roomsRes.error) throw roomsRes.error;
      if (cropsRes.error) throw cropsRes.error;

      const strainMap = new Map(
        (strainsRes.data ?? []).map((s) => [s.id, s.name as string]),
      );

      const rooms: SelectOption[] = [{ value: "", label: "Select a room…" }];
      const zones: SelectOption[] = [{ value: "", label: "Select a location…" }];

      for (const room of roomsRes.data ?? []) {
        rooms.push({ value: room.id, label: room.room_name as string });
        const roomZones = (room.zones ?? []) as Array<{
          id: string;
          zone_number: number;
        }>;
        roomZones
          .sort((a, b) => a.zone_number - b.zone_number)
          .forEach((z) => {
            zones.push({
              value: z.id,
              label: `${room.room_name} · Zone ${z.zone_number}`,
            });
          });
      }

      const crops: SelectOption[] = [{ value: "", label: "Select a crop…" }];
      for (const crop of cropsRes.data ?? []) {
        const strain =
          crop.strain_id && strainMap.get(crop.strain_id as string);
        const label = strain
          ? `${crop.code} — ${strain}`
          : (crop.code as string);
        crops.push({ value: crop.id, label });
      }

      return { rooms, zones, crops };
    },
    enabled: !!activeFacility?.id,
    staleTime: 60_000,
  });

  return { ...data, isLoading };
}
